import { useState } from "react";
import { useTranslation } from "react-i18next";
import Seo from "../../components/Seo/Seo";
import "./Contact.css";

export default function Contact() {
  const { t } = useTranslation("common");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    // Simulate form submission (replace with actual API call)
    setTimeout(() => {
      setStatus({
        type: "success",
        message: t("contact.form.status.success")
      });
      setIsSubmitting(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    }, 1500);
  };

  return (
    <section className="page">
      <Seo
        title={t("contact.seo.title")}
        description={t("contact.seo.description")}
        path="/contact"
      />

      <div className="container">
        <div className="pageHead">
          <h1>{t("contact.pageHead.title")}</h1>
          <p className="muted">
            {t("contact.pageHead.subtitle")}
          </p>
        </div>

        <div className="contactGrid">
          {/* Contact Information */}
          <div className="contactInfo">
            <div className="card pad infoCard">
              <div className="infoItem">
                <div className="infoIcon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3>{t("contact.info.email.label")}</h3>
                  <a href="mailto:info@terhal.sa">{t("contact.info.email.value")}</a>
                </div>
              </div>
            </div>

            <div className="card pad infoCard">
              <div className="infoItem">
                <div className="infoIcon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div>
                  <h3>{t("contact.info.phone.label")}</h3>
                  <a href="tel:+966123456789" dir="ltr">{t("contact.info.phone.value")}</a>
                </div>
              </div>
            </div>

            <div className="card pad infoCard">
              <div className="infoItem">
                <div className="infoIcon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h3>{t("contact.info.location.label")}</h3>
                  <p dangerouslySetInnerHTML={{ __html: t("contact.info.location.value") }}></p>
                </div>
              </div>
            </div>

            <div className="card pad infoCard">
              <div className="infoItem">
                <div className="infoIcon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div>
                  <h3>{t("contact.info.hours.label")}</h3>
                  <p dangerouslySetInnerHTML={{ __html: t("contact.info.hours.value") }}></p>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="card pad mapCard">
              <div className="mapEmbed">
                <iframe
                  title={t("contact.map.title")}
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3538.225760640655!2d41.59682758494179!3d27.52444448286798!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjfCsDMxJzI4LjAiTiA0McKwMzUnNDAuNyJF!5e0!3m2!1sar!2ssa!4v1766404398377!5m2!1sar!2ssa"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

            </div>
          </div>

          {/* Contact Form */}
          <div className="contactFormWrapper">
            <div className="card pad">
              <h2 className="formTitle">{t("contact.form.title")}</h2>
              <p className="formSubtitle">{t("contact.form.subtitle")}</p>

              <form onSubmit={handleSubmit} className="contactForm">
                <div className="formGroup">
                  <label htmlFor="name">{t("contact.form.labels.name")}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t("contact.form.placeholders.name")}
                  />
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label htmlFor="email">{t("contact.form.labels.email")}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t("contact.form.placeholders.email")}
                    />
                  </div>

                  <div className="formGroup">
                    <label htmlFor="phone">{t("contact.form.labels.phone")}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder={t("contact.form.placeholders.phone")}
                      dir="ltr"
                    />
                  </div>
                </div>

                <div className="formGroup">
                  <label htmlFor="subject">{t("contact.form.labels.subject")}</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("contact.form.options.default")}</option>
                    <option value="general">{t("contact.form.options.general")}</option>
                    <option value="partnership">{t("contact.form.options.partnership")}</option>
                    <option value="membership">{t("contact.form.options.membership")}</option>
                    <option value="complaint">{t("contact.form.options.complaint")}</option>
                    <option value="suggestion">{t("contact.form.options.suggestion")}</option>
                    <option value="other">{t("contact.form.options.other")}</option>
                  </select>
                </div>

                <div className="formGroup">
                  <label htmlFor="message">{t("contact.form.labels.message")}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder={t("contact.form.placeholders.message")}
                  ></textarea>
                </div>

                {status.message && (
                  <div className={`formStatus ${status.type}`}>
                    {status.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="submitBtn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {t("contact.form.buttons.submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact.form.buttons.submit")}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <path d="M22 2L15 22l-4-9-9-4 20-7z" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}